import { Injectable } from '@angular/core';
import { ProductSearch } from '../models/product-search.model';
import { IndianCurrencyPipe } from 'src/app/shared/pipes/indian-currency.pipe';
import { ProductListSearch } from '../models/product-listing.model';

export interface MappedFilter {
  type: string;
  filters: FilterValue[];
  label: string;
}

export interface FilterValue {
  id: string;
  value: string;
  checked: boolean;
  doc_count: number;
  price_range?: {
    from: number;
    to: number
  }
}

export enum ProductFilterType {
  Attribute = 'attribute',
  Category = 'category',
  Price = 'Price',
  Brand = 'Brand'
}

@Injectable({
  providedIn: 'root'
})
export class FiltersAdapterService {

  indianCurrencyRoundupPipe = new IndianCurrencyPipe();

  toResponse(productSearch: ProductSearch) {

    const { hits = null, _scroll_id = '', aggregations = null } = productSearch || {};
    const filter: MappedFilter[] = [];

    const categoryFilters: FilterValue[] = aggregations?.category_filter?.buckets?.map(
      (categoryFilter: any, index: number) => {
        return {
          value: categoryFilter?.key,
          checked: false,
          doc_count: categoryFilter?.doc_count,
          id: index + 'category' + categoryFilter?.key
        };
      }
    ) || [];

    if (categoryFilters?.length) {
      filter.push({
        type: ProductFilterType.Category,
        label: 'Categories',
        filters: categoryFilters
      });
    }

    aggregations?.attribute_filter?.key?.buckets?.forEach(
      (attributeGroupFilter: any) => {
        const attributeFilters = attributeGroupFilter?.value?.buckets?.map((filterValue: any, index: number) =>
        ({
          value: filterValue?.key,
          checked: false,
          doc_count: filterValue?.doc_count,
          id: (index + attributeGroupFilter?.key + filterValue?.key).split(' ').join('-')
        })
        );

        if (attributeFilters?.length) {
          filter.push({
            type: ProductFilterType.Attribute,
            label: attributeGroupFilter?.key.toLowerCase(),
            filters: attributeFilters
          });
        }
      }
    );

    const brandFilter = aggregations?.brand_filter?.buckets?.map((brandFilter: any, index: number) => {
      if (brandFilter?.doc_count) {
        return ({
          value: brandFilter?.key,
          checked: false,
          doc_count: brandFilter?.doc_count,
          id: index + 'brand' + brandFilter?.key
        })
      }
    }).filter(Boolean) as FilterValue[];

    if (brandFilter?.length) {
      filter.push({
        type: ProductFilterType.Brand,
        label: 'Brands',
        filters: brandFilter
      });
    }

    const priceRangeFilter = aggregations?.price_filter?.buckets?.map((priceFilter: any, index: number) => {
      if (priceFilter?.doc_count) {

        // Format price range properly: '₹0 - ₹1000' instead of just '₹0.00'
        const fromFormatted = this.formatValueToRupees(priceFilter?.from?.toString() || '0');
        const toFormatted = this.formatValueToRupees(priceFilter?.to?.toString() || '0');
        const priceRangeValue = `${fromFormatted} - ${toFormatted}`;

        return ({
          value: priceRangeValue,
          checked: false,
          doc_count: priceFilter?.doc_count,
          id: index + 'price' + priceFilter?.key,
          price_range: {
            from: priceFilter?.from,
            to: priceFilter?.to
          }
        })
      }
    }).filter(Boolean) as FilterValue[];

    if (priceRangeFilter?.length) {
      filter.push({
        type: ProductFilterType.Price,
        label: 'Price',
        filters: priceRangeFilter
      });
    }

    return {
      hits,
      filter,
      _scroll_id
    };
  }

  toResponseV2(productSearch: ProductListSearch) {

    const { hits = null, _scroll_id = '', aggregations = null } = productSearch || {};
    const filter: MappedFilter[] = [];

    const categoryFilters: FilterValue[] = aggregations?.category_filter?.buckets?.map(
      (categoryFilter: any, index: number) => {
        return {
          value: categoryFilter?.key,
          checked: false,
          doc_count: categoryFilter?.doc_count,
          id: index + 'category' + categoryFilter?.key
        };
      }
    ) || [];

    if (categoryFilters?.length) {
      filter.push({
        type: ProductFilterType.Category,
        label: 'Categories',
        filters: categoryFilters
      });
    }

    aggregations?.attribute_filter?.key?.buckets?.forEach(
      (attributeGroupFilter: any) => {
        const attributeFilters = attributeGroupFilter?.value?.buckets?.map((filterValue: any, index: number) =>
        ({
          value: filterValue?.key,
          checked: false,
          doc_count: filterValue?.doc_count,
          id: (index + attributeGroupFilter?.key + filterValue?.key).split(' ').join('-')
        })
        );

        if (attributeFilters?.length) {
          filter.push({
            type: ProductFilterType.Attribute,
            label: attributeGroupFilter?.key.toLowerCase(),
            filters: attributeFilters
          });
        }
      }
    );

    const brandFilter = aggregations?.brand_filter?.buckets?.map((brandFilter: any, index: number) => {
      if (brandFilter?.doc_count) {
        return ({
          value: brandFilter?.key,
          checked: false,
          doc_count: brandFilter?.doc_count,
          id: index + 'brand' + brandFilter?.key
        })
      }
    }).filter(Boolean) as FilterValue[];

    if (brandFilter?.length) {
      filter.push({
        type: ProductFilterType.Brand,
        label: 'Brands',
        filters: brandFilter
      });
    }

    const priceRangeFilter = aggregations?.price_filter?.buckets?.map((priceFilter: any, index: number) => {
      if (priceFilter?.doc_count) {

        // Format price range properly: '₹0 - ₹1000' instead of just '₹0.00'
        const fromFormatted = this.formatValueToRupees(priceFilter?.from?.toString() || '0');
        const toFormatted = this.formatValueToRupees(priceFilter?.to?.toString() || '0');
        const priceRangeValue = `${fromFormatted} - ${toFormatted}`;

        return ({
          value: priceRangeValue,
          checked: false,
          doc_count: priceFilter?.doc_count,
          id: index + 'price' + priceFilter?.key,
          price_range: {
            from: priceFilter?.from,
            to: priceFilter?.to
          }
        })
      }
    }).filter(Boolean) as FilterValue[];

    if (priceRangeFilter?.length) {
      filter.push({
        type: ProductFilterType.Price,
        label: 'Price',
        filters: priceRangeFilter
      });
    }

    return {
      hits,
      filter,
      _scroll_id
    };
  }

  private formatValueToRupees(value: string): string {
    return this.indianCurrencyRoundupPipe.transform(value);
  }
}

